import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Bar, BarChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { supabase } from "@/integrations/supabase/client";
import { ChevronDown } from "lucide-react";

type TimeRange = '7d' | '30d';

export function UserGrowthChart() {
  const { t, i18n } = useTranslation();
  const [range, setRange] = useState<TimeRange>('7d');

  const { data: profiles, isLoading } = useQuery({
    queryKey: ['user-growth', range],
    queryFn: async () => {
      const days = range === '7d' ? 7 : 30;
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - days);
      
      const { data, error } = await supabase
        .from('profiles')
        .select('created_at')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      return data || [];
    }
  });

  const chartData = useMemo(() => {
    if (!profiles || profiles.length === 0) {
      // Return empty data for each day
      const days = range === '7d' ? 7 : 30;
      const result = [];
      for (let i = days - 1; i >= 0; i--) {
        const date = new Date();
        date.setDate(date.getDate() - i);
        result.push({
          name: date.toLocaleDateString(i18n.language, { weekday: 'short' }),
          users: 0
        });
      }
      return result;
    }

    // Group by date (stable ISO key)
    const grouped: Record<string, number> = {};
    profiles.forEach((profile) => {
      const dateKey = new Date(profile.created_at).toISOString().split('T')[0];
      grouped[dateKey] = (grouped[dateKey] || 0) + 1;
    });

    // Fill in missing dates
    const days = range === '7d' ? 7 : 30;
    const result = [];
    const today = new Date();
    
    for (let i = days - 1; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      const dateKey = date.toISOString().split('T')[0];
      
      const label = range === '7d' 
        ? date.toLocaleDateString(i18n.language, { weekday: 'short' })
        : date.toLocaleDateString(i18n.language, { month: 'numeric', day: 'numeric' });
        
      result.push({
        name: label,
        users: grouped[dateKey] || 0
      });
    }
    
    return result;
  }, [profiles, range, i18n.language]);

  return (
    <div className="h-full rounded-xl border-2 border-black bg-white p-8 shadow-[4px_4px_0_#000]">
      <div className="mb-8 flex items-center justify-between">
        <h3 className="text-2xl font-black tracking-tight">{t('admin.dashboard.userGrowth.title')}</h3>
        <div className="relative">
          <select 
            value={range}
            onChange={(e) => setRange(e.target.value as TimeRange)}
            className="appearance-none rounded-lg border-2 border-black pl-3 pr-8 py-1 text-sm font-bold focus:outline-none cursor-pointer"
          >
            <option value="7d">{t('admin.dashboard.userGrowth.last7Days')}</option>
            <option value="30d">{t('admin.dashboard.userGrowth.last30Days')}</option>
          </select>
          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 pointer-events-none text-gray-500" />
        </div>
      </div>
      
      <div className="h-[300px] w-full">
        {isLoading ? (
          <div className="h-full w-full flex items-center justify-center text-gray-400 font-bold">
            {t('common.loading')}
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData}>
              <XAxis 
                dataKey="name" 
                stroke="#000" 
                fontSize={12} 
                tickLine={false} 
                axisLine={false}
              />
              <YAxis 
                stroke="#000" 
                fontSize={12} 
                tickLine={false} 
                axisLine={false} 
                tickFormatter={(value) => `${value}`}
                allowDecimals={false}
              />
              <Tooltip 
                cursor={{ fill: '#F5F0E8' }}
                contentStyle={{ 
                  backgroundColor: '#fff', 
                  border: '2px solid black', 
                  borderRadius: '8px',
                  boxShadow: '4px 4px 0 #000'
                }}
              />
              <Bar 
                dataKey="users" 
                name={t('admin.dashboard.stats.totalUsers')}
                fill="#FF6B9D" 
                radius={[4, 4, 0, 0]} 
                stroke="#000"
                strokeWidth={2}
              />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
}
